import { Box, Stack, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import {
  LiabilityPaymentCalculationQuery,
  useLiabilityPaymentCalculationQuery,
} from './graphql/LiabilityModal.generated';
import { LiabilityFormShape } from './LiabilityModal.constants';

export function CalculatedLiabilityPaymentDetails() {
  const { control } = useFormContext<LiabilityFormShape>();
  const [
    interestRate,
    paymentFrequency,
    paymentKind,
    outstandingBalance,
    paymentAmount,
    termStartDate,
    termEndDate,
  ] = useWatch({
    name: [
      'interestRate',
      'paymentFrequency',
      'paymentKind',
      'outstandingBalance',
      'paymentAmount',
      'termStartDate',
      'termEndDate',
    ],
    control,
  });

  const hasAllRequiredFields =
    [
      termStartDate,
      termEndDate,
      interestRate,
      paymentFrequency,
      paymentKind,
      outstandingBalance,
      paymentAmount,
    ].every(Boolean) &&
    // Ensure dates are valid Date objects and not invalid dates
    termStartDate instanceof Date &&
    termEndDate instanceof Date &&
    !isNaN(termStartDate.getTime()) &&
    !isNaN(termEndDate.getTime());

  const { data, error } = useLiabilityPaymentCalculationQuery({
    // no-cache because there's no ID and this is live-calculated
    fetchPolicy: 'no-cache',
    variables: {
      params: {
        interestRate: interestRate!,
        paymentFrequency: paymentFrequency,
        paymentKind: paymentKind,
        principalAmount: outstandingBalance!,
        termStart: termStartDate!,
        termEnd: termEndDate,
        paymentAmount,
      },
    },
    skip: !hasAllRequiredFields,
  });

  function getCalculatedPaymentsCopy() {
    if (!paymentFrequency || (!data && !error)) {
      return EMPTY_CONTENT_HYPHEN;
    }

    // A schema violation can null this field at runtime even though the generated
    // GraphQL type declares it as non-null.
    const calculatedData:
      | LiabilityPaymentCalculationQuery['liabilityPaymentCalculation']
      | null
      | undefined = data?.liabilityPaymentCalculation;

    if (error || !calculatedData) {
      return (
        <Box sx={{ color: COLORS.FUNCTIONAL.ERROR.DEFAULT }}>
          Error calculating payment amounts
        </Box>
      );
    }

    return formatCurrencyNoDecimals(calculatedData.finalPaymentAmount);
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={8}
    >
      <Box flexShrink={0}>
        <Typography variant="title-s" component="div">
          Calculated balloon payment
        </Typography>
      </Box>
      <Box flexGrow={1}>
        <Typography
          variant="title-s"
          sx={{ fontWeight: FONT_WEIGHTS.medium, textAlign: 'right' }}
          component="div"
        >
          {getCalculatedPaymentsCopy()}
        </Typography>
      </Box>
    </Stack>
  );
}
